import React from 'react';
import {
    Upload, FileText, ArrowLeft, Trash2, Sparkles, CheckCircle,
    Calendar, Plus, ExternalLink, FolderOpen, CloudUpload, ChevronDown,
    CheckCircle2, Info, X, Lightbulb
} from 'lucide-react';
import './RFPIntake.css';
import SuccessToast from '../../../shared/components/ui/SuccessToast';
import { useRFPIntake } from './hooks/useRFPIntake';
import AnalysisProgress from './components/AnalysisProgress';
import GoNoGoResults from './components/GoNoGoResults';
import CreateThemeModal from '../../../features/themes/components/CreateThemeModal';

const RFPIntake = () => {
    const {
        navigate, fileInputRef, files, isDragging, analyzing, analysisProgress, activeStepIndex, steps,
        queryDate, setQueryDate, dueDate, setDueDate, valuation, currency,
        toast, fetchingGoNoGo, goNoGoData, activeTab, setActiveTab, updatingDecision, selectedDecision,
        alertModal, setAlertModal, rawFiles,
        handleFileChange, handleDragOver, handleDragLeave, handleDrop, handleRemoveFile, handleStartAnalysis,
        handleGoNoGoSubmit, handleConfirmGoNoGo, isFormValid, handleValuationChange,
        templates, templatesLoading, selectedTemplateId, setSelectedTemplateId,
        projects, projectsLoading, selectedProjectIds, setSelectedProjectIds,
        selectAllProjects, setSelectAllProjects, setTemplates,
    } = useRFPIntake();

    const [showCreateTheme, setShowCreateTheme] = React.useState(false);

    if (analyzing || fetchingGoNoGo || goNoGoData) {
        return (
            <div className="rfp-intake-page">
                {goNoGoData && (
                    <div className="intake-header-refined" style={{ marginBottom: '4px' }}>
                        <button className="refined-back-btn" onClick={() => navigate('/proposals')}>
                            <ArrowLeft size={20} />
                        </button>
                        <div className="refined-header-text">
                            <h1>Proposal Evaluation</h1>
                            <p>Review the AI-generated assessment insights and determine whether to proceed.</p>
                        </div>
                    </div>
                )}
                <div className={`scanning-container ${!goNoGoData ? 'is-loading' : ''}`}>
                    <AnalysisProgress
                        fetchingGoNoGo={fetchingGoNoGo} goNoGoData={goNoGoData}
                        filesCount={files.length} analysisProgress={analysisProgress}
                        activeStepIndex={activeStepIndex} steps={steps}
                    />
                    {goNoGoData && (
                        <GoNoGoResults
                            goNoGoData={goNoGoData} activeTab={activeTab} setActiveTab={setActiveTab}
                            handleGoNoGoSubmit={handleGoNoGoSubmit} updatingDecision={updatingDecision}
                            selectedDecision={selectedDecision}
                        />
                    )}
                </div>
                {alertModal.show && <AlertModal alertModal={alertModal} setAlertModal={setAlertModal} navigate={navigate} rawFiles={rawFiles} handleConfirmGoNoGo={handleConfirmGoNoGo} />}
            </div>
        );
    }

    return (
        <div className="rfp-intake-page">
            <SuccessToast toast={toast} />

            {/* Page Header */}
            <div className="intake-page-header">
                <button className="refined-back-btn" onClick={() => navigate('/proposals')}>
                    <ArrowLeft size={18} />
                </button>
                <div>
                    <h1 className="intake-page-title">Create New RFP</h1>
                    <p className="intake-page-sub">Upload your RFP document and provide details to get started with AI-powered analysis.</p>
                </div>
            </div>

            {/* Two-column card layout */}
            <div className="intake-two-col">

                {/* ── LEFT: Upload ── */}
                <div className="intake-section-card">
                    <div className="intake-section-header">
                        <div className="intake-section-badge">1</div>
                        <span className="intake-section-title">Upload RFP Document</span>

                    </div>

                    {/* Drop Zone */}
                    <div
                        className={`intake-dropzone ${isDragging ? 'is-dragging' : ''} ${files.length > 0 ? 'has-file' : ''}`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        <div className="intake-dropzone-icon-wrap">
                            <CloudUpload size={32} color="#FF5526" />
                        </div>
                        <p className="intake-dropzone-title">Drag &amp; drop your RFP document here</p>
                        <p className="intake-dropzone-or">or</p>
                        <label className="intake-choose-btn">
                            Choose File
                            <input type="file" onChange={handleFileChange} style={{ display: 'none' }} />
                        </label>
                        <p className="intake-dropzone-hint">PDF or DOCX • Max size 50MB</p>
                    </div>

                    {/* Uploaded File Preview */}
                    {files.length > 0 && (
                        <div className="intake-file-preview">
                            <div className="intake-file-icon">
                                <FileText size={22} color="#EF4444" />
                                <span className="intake-file-ext">{files[0].type}</span>
                            </div>
                            <div className="intake-file-info">
                                <span className="intake-file-name">{files[0].name}</span>
                                <span className="intake-file-size">{files[0].size} • Uploaded just now</span>
                            </div>
                            <div className="intake-file-status">
                                <CheckCircle2 size={14} color="#16A34A" />
                                <span>Upload complete</span>
                            </div>
                            <button className="intake-file-remove" onClick={handleRemoveFile} title="Remove file">
                                <X size={14} />
                            </button>
                        </div>
                    )}

                    {/* Tips Box */}
                    <div className="intake-tips-box">
                        <div className="intake-tips-header">
                            <Lightbulb size={14} color="#FF5526" />
                            <span>Tips for best AI analysis</span>

                        </div>
                        {[
                            'Ensure the document is clear and text is readable',
                            'Include all sections and annexures',
                            'Supported formats: PDF, DOCX (Max 50MB)',
                        ].map((tip, i) => (
                            <div key={i} className="intake-tip-row">
                                <CheckCircle2 size={12} color="#FF5526" style={{ flexShrink: 0 }} />
                                <span>{tip}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── RIGHT: Details ── */}
                <div className="intake-section-card">
                    <div className="intake-section-header">
                        <div className="intake-section-badge">2</div>
                        <span className="intake-section-title">RFP Details</span>
                    </div>

                    <div className="intake-form-body">

                        {/* Valuation */}
                        <div className="intake-form-group">
                            <label className="intake-label">Approximate Valuation <span className="intake-required">*</span></label>
                            <div className="intake-valuation-row">
                                <div className="intake-currency-pill">
                                    AED <ChevronDown size={12} />
                                </div>
                                <input
                                    className="intake-input"
                                    type="text"
                                    placeholder="e.g. 300,000"
                                    value={valuation}
                                    onChange={(e) => handleValuationChange(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Proposal Submission */}
                        <div className="intake-form-group">
                            <label className="intake-label">Proposal Submission Deadline <span className="intake-required">*</span></label>
                            <div className="intake-date-row">
                                <input className="intake-input" type="date" min={new Date().toISOString().split('T')[0]} value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                            </div>
                        </div>

                        {/* Query Submission */}
                        <div className="intake-form-group">
                            <label className="intake-label">Query Submission Deadline</label>
                            <div className="intake-date-row">
                                <input className="intake-input" type="date" min={new Date().toISOString().split('T')[0]} value={queryDate} onChange={(e) => setQueryDate(e.target.value)} />
                            </div>
                        </div>

                        {/* Branding Template */}
                        <div className="intake-form-group">
                            <label className="intake-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>Branding Template <span className="intake-optional">(Optional)</span></span>
                                <button type="button" onClick={() => setShowCreateTheme(true)} className="intake-new-link">
                                    <Plus size={12} /> New Template
                                </button>
                            </label>
                            <div className="intake-select-wrap">
                                <select
                                    className="intake-select"
                                    value={selectedTemplateId}
                                    onChange={(e) => setSelectedTemplateId(e.target.value)}
                                    disabled={templatesLoading}
                                >
                                    <option value="">{templatesLoading ? 'Loading...' : 'Select a template'}</option>
                                    <option value="default">Default Theme</option>
                                    {templates
                                        .filter(t => t.id !== 'default' && t.name?.toLowerCase() !== 'default theme')
                                        .map(t => (
                                            <option key={t.id} value={t.id}>{t.name}</option>
                                        ))}
                                </select>
                                <ChevronDown size={14} className="intake-select-arrow" />
                            </div>
                        </div>

                        {/* Associated Projects */}
                        <div className="intake-form-group">
                            <label className="intake-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                Associated Projects <span className="intake-optional">(Optional)</span>
                                <Info size={13} color="#94A3B8" />
                            </label>
                            <label className="intake-check-row">
                                <input
                                    type="checkbox"
                                    checked={selectAllProjects}
                                    onChange={(e) => { setSelectAllProjects(e.target.checked); if (e.target.checked) setSelectedProjectIds([]); }}
                                    style={{ accentColor: '#FF5526', width: '14px', height: '14px' }}
                                />
                                Select All Projects
                            </label>

                            {!selectAllProjects && (
                                <>
                                    <div className="intake-select-wrap">
                                        <select
                                            className="intake-select"
                                            value=""
                                            onChange={(e) => {
                                                const pid = e.target.value;
                                                if (pid && !selectedProjectIds.includes(pid)) {
                                                    setSelectedProjectIds(prev => [...prev, pid]);
                                                }
                                            }}
                                            disabled={projectsLoading}
                                        >
                                            <option value="">{projectsLoading ? 'Loading...' : 'Select projects'}</option>
                                            {projects
                                                .filter(p => !selectedProjectIds.includes(p.project_id || p.id))
                                                .map(p => {
                                                    const pid = p.project_id || p.id;
                                                    const name = p.title || p.project_name || p.name || pid;
                                                    return <option key={pid} value={pid}>{name}</option>;
                                                })
                                            }
                                        </select>
                                        <ChevronDown size={14} className="intake-select-arrow" />
                                    </div>

                                    {/* Selected project chips */}
                                    {selectedProjectIds.length > 0 && (
                                        <div className="intake-project-chips">
                                            {selectedProjectIds.map(pid => {
                                                const p = projects.find(x => (x.project_id || x.id) === pid);
                                                const name = p?.title || p?.project_name || p?.name || pid;
                                                return (
                                                    <span key={pid} className="intake-chip">
                                                        {name}
                                                        <button onClick={() => setSelectedProjectIds(prev => prev.filter(id => id !== pid))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', color: '#64748B' }}>
                                                            <X size={11} />
                                                        </button>
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {!projectsLoading && projects.length === 0 && (
                                        <div className="intake-info-row">
                                            <Info size={13} color="#FF5526" style={{ flexShrink: 0 }} />
                                            <span>No projects available. <button type="button" onClick={() => navigate('/projects')} className="intake-new-link">Create a project to associate with this RFP.</button></span>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="intake-actions">
                        <button className="intake-cancel-btn" onClick={() => navigate('/proposals')}>
                            Cancel
                        </button>
                        <button className="intake-analyze-btn" onClick={handleStartAnalysis} disabled={!isFormValid}>
                            <Sparkles size={16} />
                            <div>
                                <div>Analyze RFP</div>
                                <div style={{ fontSize: '10px', fontWeight: 400, opacity: 0.85 }}>AI will analyze and extract insights</div>
                            </div>
                        </button>
                    </div>
                </div>
            </div>

            {/* Alert Modal */}
            {alertModal.show && <AlertModal alertModal={alertModal} setAlertModal={setAlertModal} navigate={navigate} rawFiles={rawFiles} handleConfirmGoNoGo={handleConfirmGoNoGo} />}

            {/* Create Theme Modal */}
            {showCreateTheme && (
                <CreateThemeModal
                    onClose={() => setShowCreateTheme(false)}
                    onCreated={async (newId) => {
                        try {
                            const { apiGet } = await import('../../../api/axiosClient');
                            const res = await apiGet('/templates');
                            const data = res.data?.templates || [];
                            const mapped = data.map(t => ({
                                id: t.template_id,
                                name: t.name || t.client_name || 'Unnamed Template',
                            }));
                            setTemplates(mapped);
                            if (newId) setSelectedTemplateId(newId);
                        } catch (e) {
                            console.error('Failed to refresh templates:', e);
                        }
                        setShowCreateTheme(false);
                    }}
                />
            )}
        </div>
    );
};

const AlertModal = ({ alertModal, setAlertModal, navigate, rawFiles, handleConfirmGoNoGo }) => (
    <div className="status-modal-overlay">
        <div className="status-modal-content">
            <div className="status-modal-icon"><CheckCircle size={32} color="#10b981" /></div>
            <p>{alertModal.message}</p>
            <div className="status-modal-actions" style={{ display: 'flex', gap: '10px', width: '100%', marginTop: '0.5rem' }}>
                <button className="status-modal-cancel" onClick={() => setAlertModal({ show: false, message: '', stageCode: null })} style={{ flex: 1, background: '#F1F5F9', color: '#475569', border: 'none', padding: '0.6rem 1rem', borderRadius: '10px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                <button className="status-modal-close" onClick={() => {
                    if (alertModal.isConfirmation) { handleConfirmGoNoGo(); }
                    else {
                        setAlertModal({ show: false, message: '', stageCode: null });
                        if (alertModal.stageCode === 8) { navigate(`/proposals/analysis-result/${alertModal.rfpId}`, { state: { analysisData: alertModal.rfpResData, fromUpload: true, files: rawFiles } }); }
                        else { navigate('/proposals'); }
                    }
                }} style={{ flex: 1, padding: '0.6rem 1rem' }}>Continue</button>
            </div>
        </div>
    </div>
);

export default RFPIntake;
